import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Inbox, Loader2, RefreshCw, Mail, Phone, User as UserIcon,
  Calendar as CalendarIcon, ExternalLink, CheckCircle2, Search,
  Filter as FilterIcon, X as XIcon, UserCog, Trash2, AlertTriangle,
  Clock, ChevronDown, Undo2, Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Skeleton } from '@easyflow/ui-frontend/components/ui/skeleton-loader';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@easyflow/ui-frontend/components/ui/sheet';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useSocket } from '@/contexts/SocketContext';
import type { CalendarAccess } from '@easyflow/shared';
import { canWriteCalendarObject } from '@/lib/calendar-access';
import AppointmentSourceBadge from '@/components/calendar/AppointmentSourceBadge';
import {
  APPOINTMENT_SOURCE_TAXONOMY,
  type AppointmentSourceKind,
} from '@/lib/appointment-source-taxonomy';

interface InboxItem {
  user_id?: string | null;
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  assigned_to_id?: string | null;
  assigned_to_name?: string;
  form_name?: string;
  form_slug?: string;
  type_name?: string;
  type_color?: string;
  utm?: Record<string, unknown> | null;
  // Task #595: tassonomia unificata sorgente.
  source_kind?: AppointmentSourceKind | string | null;
  inbox_handled_at?: string | null;
  inbox_rejected_at?: string | null;
  customer_id?: string | null;
  lead_id?: string | null;
  person_id?: string | null;
  created_at: string;
}

interface FilterOptions {
  forms: { id: string; name: string; slug: string }[];
  assignees: { id: string; name: string }[];
  utmSources: string[];
  // Task #595: lista kind effettivamente presenti tra le submission del tenant.
  sourceKinds: string[];
}

interface Filters {
  status: 'scheduled' | 'open' | 'all';
  formId: string;
  assignedToId: string;
  utmSource: string;
  // Task #595: multi-select sorgente (vuoto = tutte).
  sourceKinds: AppointmentSourceKind[];
  q: string;
  dateFrom: string;
  dateTo: string;
  // Quando true, l'Inbox include anche gli appuntamenti inseriti manualmente
  // (non solo quelli arrivati da form pubblici). Default false per non
  // cambiare la semantica storica del tab.
  includeAllSources: boolean;
}

const ANY = '__any__';
// v3: aggiunto includeAllSources al payload persistito.
const STORAGE_KEY = 'calendar-inbox-filters-v3';
const PAGE_SIZE = 50;

const DEFAULT_FILTERS: Filters = {
  status: 'scheduled',
  formId: ANY,
  assignedToId: ANY,
  utmSource: ANY,
  sourceKinds: [],
  q: '',
  dateFrom: '',
  dateTo: '',
  includeAllSources: false,
};

type UrgencyKey = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later';
const URGENCY_ORDER: UrgencyKey[] = ['overdue', 'today', 'tomorrow', 'week', 'later'];
const URGENCY_LABEL: Record<UrgencyKey, string> = {
  overdue: 'In ritardo',
  today: 'Oggi',
  tomorrow: 'Domani',
  week: 'Questa settimana',
  later: 'Successivi',
};
const URGENCY_BORDER: Record<UrgencyKey, string> = {
  overdue: 'border-red-500',
  today: 'border-amber-500',
  tomorrow: 'border-amber-400',
  week: 'border-blue-400',
  later: 'border-slate-300 dark:border-slate-600',
};

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function urgencyOf(iso: string, now: Date): UrgencyKey {
  const t = new Date(iso).getTime();
  const today0 = startOfDay(now).getTime();
  const tomorrow0 = today0 + 86400000;
  const dayAfter = tomorrow0 + 86400000;
  const weekEnd = today0 + 7 * 86400000;
  if (t < now.getTime()) return 'overdue';
  if (t < tomorrow0) return 'today';
  if (t < dayAfter) return 'tomorrow';
  if (t < weekEnd) return 'week';
  return 'later';
}
function relativeTime(iso: string, now: Date): string {
  const diffMs = new Date(iso).getTime() - now.getTime();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60000);
  const hr = Math.round(abs / 3600000);
  const day = Math.round(abs / 86400000);
  let s: string;
  if (min < 60) s = `${min} min`;
  else if (hr < 24) s = `${hr}h`;
  else s = `${day}g`;
  return past ? `in ritardo di ${s}` : `fra ${s}`;
}

interface Props {
  access?: CalendarAccess | null;
  canManage?: boolean;
  onOpenAppointment?: (id: string) => void;
  /** Callback per aggiornare ottimisticamente il counter del tab Inbox in Calendar.tsx */
  onCountChange?: (delta: number) => void;
  /** Apre la scheda cliente person-first per la submission selezionata. */
  onOpenCustomerCard?: (item: { person_id?: string | null; customer_id?: string | null; lead_id?: string | null; customer_name?: string; title: string }) => void;
}

type QuickFilter = null | 'today' | 'unassigned' | 'overdue';

export default function CalendarInboxTab({ access = null, canManage = false, onOpenAppointment, onCountChange, onOpenCustomerCard }: Props) {
  const { toast } = useToast();
  const { on, off, socketVersion } = useSocket();
  const [items, setItems] = useState<InboxItem[]>([]);
  const canEditItem = (item: InboxItem) => canManage && canWriteCalendarObject(access, item, 'edit');
  const canEditId = (id: string) => {
    const item = items.find(row => row.id === id);
    return !!item && canEditItem(item);
  };
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [options, setOptions] = useState<FilterOptions>({ forms: [], assignees: [], utmSources: [], sourceKinds: [] });
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [showFiltersInline, setShowFiltersInline] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null);
  const [reassignOpenId, setReassignOpenId] = useState<string | null>(null);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);
  const [now, setNow] = useState<Date>(() => new Date());
  const refreshTimer = useRef<number | null>(null);

  // Tick "now" ogni 60s per aggiornare etichette tempo relativo + raggruppamenti.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(id);
  }, []);

  const [filters, setFilters] = useState<Filters>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
    } catch {}
    return DEFAULT_FILTERS;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch {}
  }, [filters]);

  const activeCount = useMemo(() => {
    let c = 0;
    if (filters.status !== DEFAULT_FILTERS.status) c++;
    if (filters.formId !== ANY) c++;
    if (filters.assignedToId !== ANY) c++;
    if (filters.utmSource !== ANY) c++;
    if (filters.sourceKinds.length > 0) c++;
    if (filters.q.trim()) c++;
    if (filters.dateFrom) c++;
    if (filters.dateTo) c++;
    if (filters.includeAllSources) c++;
    return c;
  }, [filters]);

  const buildQuery = useCallback((f: Filters, offset = 0) => {
    const params: Record<string, string> = { limit: String(PAGE_SIZE), offset: String(offset) };
    if (f.status === 'all' || f.status === 'open') params.status = f.status;
    if (f.formId && f.formId !== ANY) params.formId = f.formId;
    if (f.assignedToId && f.assignedToId !== ANY) params.assignedToId = f.assignedToId;
    if (f.utmSource && f.utmSource !== ANY) params.utmSource = f.utmSource;
    if (f.sourceKinds.length > 0) params.sourceKinds = f.sourceKinds.join(',');
    if (f.q.trim()) params.q = f.q.trim();
    if (f.dateFrom) params.dateFrom = f.dateFrom;
    if (f.dateTo) params.dateTo = f.dateTo;
    if (f.includeAllSources) params.includeAllSources = 'true';
    return `?${new URLSearchParams(params).toString()}`;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/calendar/inbox${buildQuery(filters, 0)}`);
      if (res?.success) {
        setItems((res.data as InboxItem[] | undefined) || []);
        // `hasMore` è un campo extra dell'envelope inbox, non presente in ApiResponse.
        setHasMore(!!(res as { hasMore?: boolean }).hasMore);
        setSelected(new Set());
      }
    } catch (e) {
      console.error('Inbox load failed', e);
    } finally { setLoading(false); }
  }, [buildQuery, filters]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await apiClient.get(`/calendar/inbox${buildQuery(filters, items.length)}`);
      if (res?.success) {
        const more = (res.data as InboxItem[] | undefined) || [];
        setItems(prev => [...prev, ...more]);
        setHasMore(!!(res as { hasMore?: boolean }).hasMore);
      }
    } catch (e) {
      console.error('Inbox load more failed', e);
    } finally { setLoadingMore(false); }
  }, [buildQuery, filters, items.length, hasMore, loadingMore]);

  const loadOptions = useCallback(async () => {
    try {
      const res = await apiClient.get('/calendar/inbox/filters/options');
      if (res?.success && res.data) setOptions(res.data as FilterOptions);
    } catch (e) { console.error('Inbox filter options load failed', e); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  // Debounce socket: riconcilia con backend dopo 800ms da un evento
  // (evita raffiche di fetch quando il bulk emette in serie).
  useEffect(() => {
    const handler = () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => load(), 800);
    };
    on?.('calendar:appointment_changed', handler);
    return () => {
      off?.('calendar:appointment_changed', handler);
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    };
  }, [on, off, socketVersion, load]);

  // ---- Helpers stato locale ottimistico ----
  /** Rimuove le righe se i filtri non includerebbero più handled/rejected. */
  const removeOrUpdate = (ids: string[], patch: Partial<InboxItem>) => {
    const keep = filters.status === 'all';
    if (keep) {
      setItems(prev => prev.map(i => (ids.includes(i.id) ? { ...i, ...patch } : i)));
    } else {
      setItems(prev => prev.filter(i => !ids.includes(i.id)));
    }
    setSelected(prev => {
      const n = new Set(prev);
      ids.forEach(id => n.delete(id));
      return n;
    });
  };

  // ---- Azioni singola riga ----
  const markHandled = async (id: string) => {
    if (!canEditId(id)) return;
    setActingId(id);
    try {
      const res = await apiClient.post(`/calendar/inbox/${id}/mark-handled`, {});
      if (res?.success) {
        toast({ title: 'Richiesta gestita', description: 'Spostata in stato confermato.' });
        removeOrUpdate([id], { status: 'confirmed', inbox_handled_at: new Date().toISOString() });
        onCountChange?.(-1);
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Operazione non riuscita', variant: 'destructive' });
    } finally { setActingId(null); }
  };

  const reject = async (id: string) => {
    if (!canEditId(id)) return;
    setActingId(id);
    try {
      const res = await apiClient.post(`/calendar/inbox/${id}/reject`, {});
      if (res?.success) {
        toast({ title: 'Richiesta rifiutata', description: 'Annullata e rimossa dalla coda.' });
        removeOrUpdate([id], {
          status: 'cancelled',
          inbox_handled_at: new Date().toISOString(),
          inbox_rejected_at: new Date().toISOString(),
        });
        onCountChange?.(-1);
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Operazione non riuscita', variant: 'destructive' });
    } finally { setActingId(null); setRejectId(null); }
  };

  // Task #578: ripristina una submission rifiutata, riportandola in coda Inbox.
  // Aggiornamento ottimistico: se i filtri non includono più la riga (es. era
  // visibile solo grazie a "Tutti gli stati" attivo), la riga resta
  // visibile come pending, altrimenti viene rimossa dalla lista.
  const restore = async (id: string) => {
    if (!canEditId(id)) return;
    setActingId(id);
    try {
      const res = await apiClient.post(`/calendar/inbox/${id}/restore`, {});
      if (res?.success) {
        toast({ title: 'Prenotazione ripristinata', description: 'Riportata nella coda Inbox.' });
        // Aggiorna la riga in-place: ridiventa pending. Se i filtri non
        // mostrerebbero più la riga (es. status diverso da 'all'/'open'),
        // resta comunque visibile finché non si ricarica.
        setItems(prev => prev.map(i => (
          i.id === id
            ? { ...i, status: 'scheduled', inbox_handled_at: null, inbox_rejected_at: null }
            : i
        )));
        setSelected(prev => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
        onCountChange?.(+1);
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Operazione non riuscita', variant: 'destructive' });
    } finally { setActingId(null); setRestoreId(null); }
  };

  const reassign = async (id: string, assignedToId: string | null) => {
    if (!canEditId(id)) return;
    if (assignedToId && !canWriteCalendarObject(access, { assigned_to_id: assignedToId }, 'edit')) return;
    setActingId(id);
    try {
      // Task #579: usa l'endpoint dedicato Inbox: gestisce idempotenza
      // (no-op silenzioso se l'assegnatario non cambia) e fa partire
      // la notifica push/email al consulente target.
      const res = await apiClient.post(`/calendar/inbox/${id}/reassign`, { assignedToId });
      if (res?.success) {
        const name = assignedToId ? options.assignees.find(a => a.id === assignedToId)?.name : undefined;
        toast({
          title: 'Riassegnato',
          description: assignedToId ? `Ora assegnato a ${name || 'consulente selezionato'}.` : 'Assegnazione rimossa.',
        });
        setItems(prev => prev.map(i => (
          i.id === id ? { ...i, assigned_to_id: assignedToId, assigned_to_name: name } : i
        )));
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Operazione non riuscita', variant: 'destructive' });
    } finally { setActingId(null); setReassignOpenId(null); }
  };

  // ---- Azioni bulk ----
  const bulkAction = async (
    action: 'mark-handled' | 'reject' | 'reassign' | 'restore',
    extra: { assignedToId?: string | null } = {},
  ) => {
    if (!canManage) return;
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!ids.every(canEditId)) return;
    if (action === 'reassign' && extra.assignedToId
      && !canWriteCalendarObject(access, { assigned_to_id: extra.assignedToId }, 'edit')) return;
    setBulkBusy(true);
    try {
      const res = await apiClient.post<{ ids?: string[]; affected?: number }>(
        '/calendar/inbox/bulk',
        { ids, action, ...extra },
      );
      if (res?.success) {
        // Usa SOLO gli id realmente aggiornati dal backend per il counter:
        // il backend filtra per scope inbox + stato pending, quindi voci
        // già gestite/rifiutate selezionate (in modalità "Tutti gli stati")
        // non vengono conteggiate due volte.
        const affectedIds: string[] = Array.isArray(res.data?.ids) ? res.data.ids : ids;
        const n = res.data?.affected ?? affectedIds.length;
        if (action === 'mark-handled') {
          toast({ title: `Richieste confermate: ${n}` });
          removeOrUpdate(affectedIds, { status: 'confirmed', inbox_handled_at: new Date().toISOString() });
          onCountChange?.(-n);
        } else if (action === 'reject') {
          toast({ title: `Richieste rifiutate: ${n}` });
          removeOrUpdate(affectedIds, {
            status: 'cancelled',
            inbox_handled_at: new Date().toISOString(),
            inbox_rejected_at: new Date().toISOString(),
          });
          onCountChange?.(-n);
        } else if (action === 'restore') {
          // Task #578: ripristino bulk. Le righe restano visibili come pending
          // (lo scope frontend "rifiutate" non è un filtro di lista, quindi
          // basta aggiornarle in-place).
          toast({ title: `${n} prenotazioni ripristinate` });
          setItems(prev => prev.map(i => (
            affectedIds.includes(i.id)
              ? { ...i, status: 'scheduled', inbox_handled_at: null, inbox_rejected_at: null }
              : i
          )));
          setSelected(prev => {
            const nset = new Set(prev);
            affectedIds.forEach((id: string) => nset.delete(id));
            return nset;
          });
          onCountChange?.(+n);
        } else {
          const name = extra.assignedToId
            ? options.assignees.find(a => a.id === extra.assignedToId)?.name
            : undefined;
          toast({ title: `Richieste riassegnate: ${n}`, description: name });
          setItems(prev => prev.map(i => (
            ids.includes(i.id)
              ? { ...i, assigned_to_id: extra.assignedToId || null, assigned_to_name: name }
              : i
          )));
          setSelected(new Set());
        }
      }
    } catch (e) {
      toast({ title: 'Errore', description: (e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Operazione non riuscita', variant: 'destructive' });
    } finally {
      setBulkBusy(false);
      setBulkReassignOpen(false);
      setBulkRejectOpen(false);
      setBulkRestoreOpen(false);
    }
  };

  // ---- Quick filter + raggruppamento ----
  const quickFiltered = useMemo(() => {
    if (!quickFilter) return items;
    return items.filter(i => {
      const u = urgencyOf(i.start_time, now);
      if (quickFilter === 'today') return u === 'today';
      if (quickFilter === 'overdue') return u === 'overdue';
      if (quickFilter === 'unassigned') return !i.assigned_to_id;
      return true;
    });
  }, [items, quickFilter, now]);

  const summary = useMemo(() => {
    let total = items.length;
    let today = 0, overdue = 0, unassigned = 0;
    items.forEach(i => {
      const u = urgencyOf(i.start_time, now);
      if (u === 'today') today++;
      if (u === 'overdue') overdue++;
      if (!i.assigned_to_id) unassigned++;
    });
    return { total, today, overdue, unassigned };
  }, [items, now]);

  const grouped = useMemo(() => {
    const map: Record<UrgencyKey, InboxItem[]> = {
      overdue: [], today: [], tomorrow: [], week: [], later: [],
    };
    quickFiltered.forEach(i => { map[urgencyOf(i.start_time, now)].push(i); });
    return URGENCY_ORDER.map(k => ({ key: k, items: map[k] })).filter(g => g.items.length > 0);
  }, [quickFiltered, now]);

  const visibleIds = useMemo(() => quickFiltered
    .filter(item => canManage && canWriteCalendarObject(access, item, 'edit'))
    .map(i => i.id), [quickFiltered, canManage, access]);
  // Task #578: la bulk action "Ripristina" si mostra solo se la selezione
  // contiene almeno una riga effettivamente rifiutata. Il backend filtra
  // comunque per scope, ma evitiamo di mostrare l'azione quando è inutile.
  const selectedHasRejected = useMemo(() => {
    if (selected.size === 0) return false;
    return items.some(i => selected.has(i.id) && !!i.inbox_rejected_at);
  }, [items, selected]);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (!canManage) return;
    setSelected(prev => {
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  };
  const toggleOne = (id: string) => {
    if (!canEditId(id)) return;
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });

  const reset = () => { setFilters(DEFAULT_FILTERS); setQuickFilter(null); };
  const update = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFilters(prev => ({ ...prev, [key]: value }));

  // ---- Sub-renders ----
  const FiltersGrid = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="space-y-1">
        <Label htmlFor="inbox-q" className="text-xs text-muted-foreground">Cerca</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="inbox-q"
            placeholder="Nome, email, telefono…"
            value={filters.q}
            onChange={e => update('q', e.target.value)}
            className="pl-8 h-9"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Form di origine</Label>
        <Select value={filters.formId} onValueChange={v => update('formId', v)}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Tutti i form" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Tutti i form</SelectItem>
            {options.forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Risorsa assegnata</Label>
        <Select value={filters.assignedToId} onValueChange={v => update('assignedToId', v)}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Tutte</SelectItem>
            <SelectItem value="unassigned">Non assegnate</SelectItem>
            {options.assignees.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Sorgente UTM</Label>
        <Select value={filters.utmSource} onValueChange={v => update('utmSource', v)}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Tutte</SelectItem>
            {options.utmSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Task #595: multi-select tassonomia sorgenti (9 tipi unificati). */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Tipo sorgente</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="field"
              className="h-9 w-full justify-between"
            >
              <span className="truncate">
                {filters.sourceKinds.length === 0
                  ? 'Tutti i tipi'
                  : filters.sourceKinds.length === 1
                    ? (APPOINTMENT_SOURCE_TAXONOMY.find(t => t.kind === filters.sourceKinds[0])?.label || filters.sourceKinds[0])
                    : `${filters.sourceKinds.length} selezionati`}
              </span>
              <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-1" align="start">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-xs font-medium text-muted-foreground">Filtra per sorgente</span>
              {filters.sourceKinds.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => update('sourceKinds', [])}
                >Azzera</button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {APPOINTMENT_SOURCE_TAXONOMY.map(t => {
                const checked = filters.sourceKinds.includes(t.kind);
                const present = options.sourceKinds.includes(t.kind);
                return (
                  <button
                    key={t.kind}
                    type="button"
                    onClick={() => {
                      const next = checked
                        ? filters.sourceKinds.filter(k => k !== t.kind)
                        : [...filters.sourceKinds, t.kind];
                      update('sourceKinds', next);
                    }}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-accent text-left ${
                      checked ? 'bg-accent/60' : ''
                    } ${!present ? 'opacity-50' : ''}`}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <AppointmentSourceBadge kind={t.kind} iconOnly />
                    <span className="flex-1 truncate">{t.label}</span>
                    {!present && (
                      <span className="text-[10px] text-muted-foreground">(0)</span>
                    )}
                    {checked && <Check className="w-3 h-3 text-primary" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Stato</Label>
        <Select value={filters.status} onValueChange={v => update('status', v as Filters['status'])}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled">Solo da gestire</SelectItem>
            <SelectItem value="open">Aperti (non in stato definitivo)</SelectItem>
            <SelectItem value="all">Tutti gli stati</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="inbox-from" className="text-xs text-muted-foreground">Da</Label>
        <Input id="inbox-from" type="date" value={filters.dateFrom}
          onChange={e => update('dateFrom', e.target.value)} className="h-9" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="inbox-to" className="text-xs text-muted-foreground">A</Label>
        <Input id="inbox-to" type="date" value={filters.dateTo}
          onChange={e => update('dateTo', e.target.value)} className="h-9" />
      </div>
      <div className="space-y-1 flex flex-col justify-end">
        <div className="flex items-center gap-2">
          <Checkbox
            id="inbox-include-all-sources"
            checked={filters.includeAllSources}
            onCheckedChange={v => update('includeAllSources', !!v)}
          />
          <Label htmlFor="inbox-include-all-sources" className="text-xs text-muted-foreground cursor-pointer">
            Mostra anche manuali (tutte le origini)
          </Label>
        </div>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={reset} className="h-7 px-2 text-xs justify-start">
            <XIcon className="w-3 h-3 mr-1" /> Azzera filtri
          </Button>
        )}
      </div>
    </div>
  );

  const ReassignPopover = ({ id, currentId, children }: {
    id: string; currentId?: string | null; children: React.ReactNode;
  }) => (
    <Popover open={canEditId(id) && reassignOpenId === id} onOpenChange={o => setReassignOpenId(o && canEditId(id) ? id : null)}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <div className="text-xs font-medium px-2 py-1 text-muted-foreground">Assegna a…</div>
        <div className="max-h-60 overflow-y-auto">
          <button
            type="button"
            className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent ${!currentId ? 'bg-accent/50' : ''}`}
            onClick={() => reassign(id, null)}
          >
            <span className="text-muted-foreground">— Non assegnato</span>
          </button>
          {options.assignees.map(a => (
            <button
              key={a.id}
              type="button"
              disabled={!canWriteCalendarObject(access, { assigned_to_id: a.id }, 'edit')}
              className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent ${currentId === a.id ? 'bg-accent/50 font-medium' : ''}`}
              onClick={() => reassign(id, a.id)}
            >{a.name}</button>
          ))}
          {options.assignees.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
              Nessuna risorsa disponibile
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  const renderItem = (item: InboxItem) => {
    const handled = !!item.inbox_handled_at;
    const rejected = !!item.inbox_rejected_at;
    const u = urgencyOf(item.start_time, now);
    const borderClass = handled || rejected ? 'border-emerald-400' : URGENCY_BORDER[u];
    const bgClass = rejected
      ? 'bg-slate-50/40 dark:bg-slate-900/10 opacity-75'
      : handled
        ? 'bg-emerald-50/40 dark:bg-emerald-900/10'
        : (u === 'overdue' ? 'bg-red-50/30 dark:bg-red-900/10'
          : (u === 'today' ? 'bg-amber-50/40 dark:bg-amber-900/10' : 'bg-card'));
    const checked = selected.has(item.id);
    const utm = item.utm || {};
    const utmFull = Object.entries(utm).filter(([_, v]) => v != null && v !== '');

    return (
      <div
        key={item.id}
        className={`border-l-4 ${borderClass} ${bgClass} rounded-md p-3 sm:p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border border-border/50`}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Checkbox
            disabled={!canEditItem(item)}
            checked={checked}
            onCheckedChange={() => toggleOne(item.id)}
            aria-label={`Seleziona ${item.title}`}
            className="mt-1 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-foreground truncate">{item.title}</h4>
              {!handled && !rejected && (
                <Badge
                  variant="outline"
                  className={`text-[10px] gap-1 ${
                    u === 'overdue' ? 'border-red-400 text-red-700 dark:text-red-300'
                    : u === 'today' ? 'border-amber-400 text-amber-700 dark:text-amber-300'
                    : 'border-slate-300 text-muted-foreground'
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {relativeTime(item.start_time, now)}
                </Badge>
              )}
              {rejected && (
                <Badge className="text-[10px] bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  Rifiutata
                </Badge>
              )}
              {handled && !rejected && (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-300">
                  Gestita
                </Badge>
              )}
              {/* Badge compatte: visibili anche su mobile, troncate aggressive sotto sm */}
              <div className="flex items-center gap-1 sm:gap-1.5 min-w-0 max-w-full overflow-hidden">
                {/* Task #595: badge tassonomia sorgente unificata. */}
                {item.source_kind && <AppointmentSourceBadge kind={item.source_kind} />}
                {item.form_name && (
                  <Badge variant="outline" className="text-[10px] truncate max-w-[80px] sm:max-w-[140px]">
                    {item.form_name}
                  </Badge>
                )}
                {item.type_name && (
                  <Badge
                    className="text-[10px] border truncate max-w-[70px] sm:max-w-[100px]"
                    style={item.type_color ? {
                      backgroundColor: `${item.type_color}20`,
                      color: item.type_color,
                      borderColor: `${item.type_color}50`,
                    } : undefined}
                  >{item.type_name}</Badge>
                )}
                {utmFull.length > 0 && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-[10px] cursor-help truncate max-w-[80px] sm:max-w-none">
                          <span className="sm:hidden">UTM</span>
                          <span className="hidden sm:inline">UTM: {String(utm.source || utm.medium || 'altro')}</span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs max-w-xs">
                        {utmFull.map(([k, v]) => (
                          <div key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> {fmtDate(item.start_time)}
              </span>
              {item.customer_name && (
                <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
                  <UserIcon className="w-3 h-3" /> {item.customer_name}
                </span>
              )}
              {item.customer_email && (
                <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
                  <Mail className="w-3 h-3" /> {item.customer_email}
                </span>
              )}
              {item.customer_phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {item.customer_phone}
                </span>
              )}
              {item.assigned_to_name
                ? <span>· Assegnato a {item.assigned_to_name}</span>
                : <span className="text-amber-600 dark:text-amber-400">· Non assegnato</span>}
            </div>
          </div>
        </div>

        <div className="flex gap-1.5 sm:gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap justify-end">
          {onOpenAppointment && (
            <Button size="sm" variant="outline" onClick={() => onOpenAppointment(item.id)}
              aria-label="Apri appuntamento">
              <ExternalLink className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Apri</span>
            </Button>
          )}
          {onOpenCustomerCard && (item.person_id || item.customer_id || item.lead_id) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpenCustomerCard({
                person_id: item.person_id,
                customer_id: item.customer_id,
                lead_id: item.lead_id,
                customer_name: item.customer_name,
                title: item.title,
              })}
              aria-label="Apri scheda cliente"
              data-testid={`btn-inbox-customer-card-${item.id}`}
              className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400"
            >
              <UserIcon className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Scheda</span>
            </Button>
          )}
          <ReassignPopover id={item.id} currentId={item.assigned_to_id}>
            <Button size="sm" variant="outline" disabled={!canEditItem(item) || actingId === item.id}
              aria-label="Riassegna">
              <UserCog className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Riassegna</span>
            </Button>
          </ReassignPopover>
          {!handled && !rejected && (
            <>
              <Button size="sm" variant="outline" onClick={() => setRejectId(item.id)}
                disabled={!canEditItem(item) || actingId === item.id}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                aria-label="Rifiuta">
                <Trash2 className="w-4 h-4" />
                <span className="ml-1 hidden sm:inline">Rifiuta</span>
              </Button>
              <Button
                size="sm"
                onClick={() => markHandled(item.id)}
                disabled={!canEditItem(item) || actingId === item.id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                aria-label="Conferma"
              >
                {actingId === item.id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <CheckCircle2 className="w-4 h-4" />}
                <span className="ml-1 hidden sm:inline">Conferma</span>
              </Button>
            </>
          )}
          {/* Task #578: pulsante Ripristina visibile solo per righe rifiutate
              (inbox_rejected_at presente). Riporta la submission in coda. */}
          {rejected && (
            <Button
              size="sm"
              onClick={() => setRestoreId(item.id)}
              disabled={!canEditItem(item) || actingId === item.id}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              aria-label="Ripristina"
            >
              {actingId === item.id
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Undo2 className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">Ripristina</span>
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ---- Render principale ----
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 gap-2">
        <CardTitle className="flex items-center gap-2 min-w-0">
          <Inbox className="w-5 h-5 text-purple-500 flex-shrink-0" />
          <span className="truncate">Inbox — Richieste da gestire</span>
          {summary.total > 0 && (
            <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-300 flex-shrink-0">
              {summary.total}
            </Badge>
          )}
        </CardTitle>
        <div className="flex gap-2">
          {/* Mobile: Sheet */}
          <Sheet open={showFiltersSheet} onOpenChange={setShowFiltersSheet}>
            <SheetTrigger asChild>
              <Button
                variant={activeCount > 0 ? 'default' : 'outline'}
                size="sm"
                className="md:hidden"
                aria-label="Apri filtri"
              >
                <FilterIcon className="w-4 h-4 mr-1" />
                Filtri
                {activeCount > 0 && (
                  <Badge className="ml-2 h-5 px-1.5 bg-white/20 text-white border-white/30">
                    {activeCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
              <SheetHeader><SheetTitle>Filtri Inbox</SheetTitle></SheetHeader>
              <div className="mt-4">{FiltersGrid}</div>
            </SheetContent>
          </Sheet>
          {/* Desktop: inline toggle */}
          <Button
            variant={showFiltersInline || activeCount > 0 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFiltersInline(v => !v)}
            className="hidden md:inline-flex"
          >
            <FilterIcon className="w-4 h-4 mr-1" />
            Filtri
            {activeCount > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-white/20 text-white border-white/30">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            aria-label="Ricarica">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>

      {/* Barra di sintesi quick-filter */}
      {!loading && summary.total > 0 && (
        <div className="px-4 sm:px-6 pb-2 flex flex-wrap gap-2 text-xs">
          <button
            className={`px-2.5 py-1 rounded-full border transition ${
              !quickFilter ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card hover:bg-accent border-border'
            }`}
            onClick={() => setQuickFilter(null)}
          >
            Tutti <span className="font-semibold ml-1">{summary.total}</span>
          </button>
          <button
            className={`px-2.5 py-1 rounded-full border transition ${
              quickFilter === 'today' ? 'bg-amber-500 text-white border-amber-500'
              : 'bg-card hover:bg-accent border-border'
            }`}
            onClick={() => setQuickFilter(quickFilter === 'today' ? null : 'today')}
          >
            Oggi <span className="font-semibold ml-1">{summary.today}</span>
          </button>
          <button
            className={`px-2.5 py-1 rounded-full border transition ${
              quickFilter === 'overdue' ? 'bg-red-500 text-white border-red-500'
              : 'bg-card hover:bg-accent border-border'
            }`}
            onClick={() => setQuickFilter(quickFilter === 'overdue' ? null : 'overdue')}
          >
            <AlertTriangle className="w-3 h-3 inline mr-0.5" />
            In ritardo <span className="font-semibold ml-1">{summary.overdue}</span>
          </button>
          <button
            className={`px-2.5 py-1 rounded-full border transition ${
              quickFilter === 'unassigned' ? 'bg-purple-500 text-white border-purple-500'
              : 'bg-card hover:bg-accent border-border'
            }`}
            onClick={() => setQuickFilter(quickFilter === 'unassigned' ? null : 'unassigned')}
          >
            Non assegnate <span className="font-semibold ml-1">{summary.unassigned}</span>
          </button>
        </div>
      )}

      {/* Pannello filtri inline desktop */}
      {showFiltersInline && (
        <div className="px-6 pb-4 border-b border-border hidden md:block">{FiltersGrid}</div>
      )}

      {/* Bulk action bar */}
      {canManage && selected.size > 0 && (
        <div className="sticky top-0 z-10 mx-4 sm:mx-6 mt-2 mb-1 px-3 py-2 bg-primary text-primary-foreground rounded-md flex flex-wrap items-center gap-2 shadow-md">
          <Checkbox
            checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
            onCheckedChange={toggleAll}
            aria-label="Seleziona tutto visibile"
            className="border-primary-foreground data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary"
          />
          <span className="text-sm font-medium">{selected.size} selezionate</span>
          <div className="flex gap-1.5 flex-wrap ml-auto">
            <Button size="sm" variant="secondary" onClick={() => bulkAction('mark-handled')}
              disabled={bulkBusy}>
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Conferma
            </Button>
            <Popover open={bulkReassignOpen} onOpenChange={setBulkReassignOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="secondary" disabled={bulkBusy}>
                  <UserCog className="w-4 h-4 mr-1" /> Riassegna
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="end">
                <div className="text-xs font-medium px-2 py-1 text-muted-foreground">
                  Assegna {selected.size} a…
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <button
                    type="button"
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent text-muted-foreground"
                    onClick={() => bulkAction('reassign', { assignedToId: null })}
                  >— Non assegnato</button>
                  {options.assignees.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      disabled={!canWriteCalendarObject(access, { assigned_to_id: a.id }, 'edit')}
                      className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent"
                      onClick={() => bulkAction('reassign', { assignedToId: a.id })}
                    >{a.name}</button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button size="sm" variant="destructive" onClick={() => setBulkRejectOpen(true)}
              disabled={bulkBusy}>
              <Trash2 className="w-4 h-4 mr-1" /> Rifiuta
            </Button>
            {/* Task #578: Ripristina visibile solo se la selezione contiene
                almeno una submission rifiutata. */}
            {selectedHasRejected && (
              <Button size="sm" variant="secondary" onClick={() => setBulkRestoreOpen(true)}
                disabled={bulkBusy}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Undo2 className="w-4 h-4 mr-1" /> Ripristina
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}
              className="text-primary-foreground hover:bg-primary-foreground/10">
              Deseleziona
            </Button>
          </div>
        </div>
      )}

      <CardContent className="px-4 sm:px-6">
        {loading ? (
          <div className="space-y-3" aria-label="Caricamento">
            {[0, 1, 2].map(i => (
              <div key={i} className="border-l-4 border-slate-200 dark:border-slate-700 rounded-md p-4 border border-border/50">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  <Skeleton className="h-8 w-20 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="font-medium">
              {quickFilter
                ? 'Nessuna richiesta per questo filtro rapido'
                : activeCount > 0
                  ? 'Nessuna richiesta corrisponde ai filtri'
                  : 'Nessuna richiesta da gestire'}
            </p>
            <p className="text-sm mt-1">
              {quickFilter
                ? 'Prova un altro filtro o "Tutti".'
                : activeCount > 0
                  ? 'Prova ad allargare i filtri o azzerali.'
                  : 'Le nuove prenotazioni dai form pubblici compariranno qui.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Toggle "select all visible" sopra la prima sezione (visibile sempre) */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                disabled={!canManage}
                checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
                onCheckedChange={toggleAll}
                aria-label="Seleziona tutte le visibili"
                id="inbox-select-all"
              />
              <Label htmlFor="inbox-select-all" className="cursor-pointer text-xs">
                Seleziona tutte ({visibleIds.length})
              </Label>
            </div>
            {grouped.map(group => (
              <div key={group.key} className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className={`w-2 h-2 rounded-full ${
                    group.key === 'overdue' ? 'bg-red-500'
                    : group.key === 'today' ? 'bg-amber-500'
                    : group.key === 'tomorrow' ? 'bg-amber-400'
                    : group.key === 'week' ? 'bg-blue-400' : 'bg-slate-400'
                  }`} />
                  {URGENCY_LABEL[group.key]}
                  <Badge variant="outline" className="text-[10px] ml-1">{group.items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {group.items.map(renderItem)}
                </div>
              </div>
            ))}

            {hasMore && !quickFilter && (
              <div className="flex justify-center pt-2">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carico…</>
                    : <>Carica altre <ChevronDown className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Dialog rifiuto singolo */}
      <AlertDialog open={!!rejectId && canEditId(rejectId)} onOpenChange={o => !o && setRejectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rifiutare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>
              La prenotazione verrà annullata e rimossa dalla coda Inbox. Potrai ritrovarla
              impostando lo Stato su "Tutti gli stati".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => rejectId && reject(rejectId)}
            >Rifiuta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task #578: Dialog ripristino singolo */}
      <AlertDialog open={!!restoreId && canEditId(restoreId)} onOpenChange={o => !o && setRestoreId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare la prenotazione?</AlertDialogTitle>
            <AlertDialogDescription>
              La richiesta tornerà nella coda Inbox in stato "da gestire".
              Questa azione annulla un rifiuto precedente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => restoreId && restore(restoreId)}
            >Ripristina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task #578: Dialog ripristino bulk */}
      <AlertDialog open={canManage && bulkRestoreOpen} onOpenChange={setBulkRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare le prenotazioni rifiutate?</AlertDialogTitle>
            <AlertDialogDescription>
              Solo le voci selezionate effettivamente rifiutate verranno
              riportate in coda Inbox. Le altre saranno ignorate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => bulkAction('restore')}
            >Ripristina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog rifiuto bulk */}
      <AlertDialog open={canManage && bulkRejectOpen} onOpenChange={setBulkRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rifiutare le richieste selezionate ({selected.size})?</AlertDialogTitle>
            <AlertDialogDescription>
              Tutte le prenotazioni selezionate verranno annullate e rimosse dalla coda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => bulkAction('reject')}
            >Rifiuta tutte</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
